import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import Container from '../../components/Container';
import DeliveryAlert from '../../components/DeliveryAlert';
import Catalog from '../../components/Catalog';
import StockGoods from '../../components/StockGoods';
import MainSlider from '../../components/MainSlider';
import FreeSaucesPanel from '../../components/FreeSaucesPanel';
import ThanksForOrderingLink from '../../components/ThanksForOrderingLink';
import AddressButton from '../../components/AddressButton/MobileView';
import CategoryNavDesktop from '../../components/CategoryNavDesktop';
import MainSidebar from '../../components/MainSidebar';
import CategoryNavMobile from '../../components/CategoryNavMobile';
import PromocodeWidget from '../../components/PromocodeWidget';
import FloatingCart from '../../components/FloatingCart';
import Heading from '../../components/Heading';
import NotAllowedCityModal from '../../components/NotAllowedCityModal';

// State
import { fetchBannersIsNeeded } from '../../state/modules/banner/actions';
import { toggleHomeCategories } from '../../state/modules/ui/actions';

// Config
import config from '../../config';

// Styles
import styles from './Home.styl';
import { priceIncludingBonusesSelector } from '../../state/modules/order/selectors';
import { fetchUpsale } from '../../state/modules/catalog/actions';
import { LogoMoto } from './partial/LogoMoto';
import { CurveLogo } from './partial/CurveLogo';

const cx = classNames.bind(styles);

export default function HomePage(props) {
  const { stockRole } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const { title, name, declension, _supertag } = useSelector(state => state.city.userCity);
  const { isPhone, isTablet } = useSelector(state => state.responsive);
  const isWaitingForOpenings = useSelector(state => state.city.waitOpenings);
  const priceIncludingBonuses = useSelector(priceIncludingBonusesSelector);
  const stockCode = useSelector(state => state.stock.data.code);

  useEffect(() => {
    // запрашиваем список товаров в апсейл. переспрашиваем при каждом изменении стоимости корзины или смене промокода
    dispatch(fetchUpsale());
  }, [priceIncludingBonuses, stockCode]);
  const homePageInnerRef = useRef(null);
  const mobileTopRef = useRef(null);
  const waypointRef = useRef(false);

  const isMobile = isPhone || isTablet;

  useEffect(() => {
    dispatch(fetchBannersIsNeeded());
  }, []);

  useEffect(() => {
    // при пересечении topRef выводить хэдер с категориями
    const handleScroll = () => {
      const topRef = isMobile ? mobileTopRef : homePageInnerRef;

      if (topRef.current) {
        const offsetTop = topRef.current.getBoundingClientRect().top;

        if (offsetTop < 0 && !waypointRef.current) {
          waypointRef.current = true;
          return dispatch(toggleHomeCategories(true));
        }

        if (offsetTop >= 0 && waypointRef.current) {
          waypointRef.current = false;
          return dispatch(toggleHomeCategories(false));
        }
      }

      return false;
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      waypointRef.current = false;
      dispatch(toggleHomeCategories(false));
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({ id: 'page.menu.helmet' }, { declension, name })}</title>
        <meta
          name="description"
          content={intl.formatMessage({ id: 'page.menu.helmet.description' }, { declension, name })}
        />
        {config.legal === 'ru' && (
          <link rel="canonical" href={`https://papajohns.ru/${_supertag}`} />
        )}
      </Helmet>
      <div className={cx('HomePage')}>
        <span ref={mobileTopRef} />
        {!isWaitingForOpenings && (
          <>
            <Container className={cx('HomePage__banner')}>
              <Heading tagName="h1" className={cx('HomePage__h1')}>
                <div className={cx('HomePage__firstPart')}>
                  {intl.formatMessage({ id: 'page.menu.header.title' })}
                </div>
                <div className={cx('HomePage__firstPart_logo')}>
                  <LogoMoto />
                </div>
                <div className={cx('HomePage__secondPart')}>
                  {declension.toUpperCase()}
                  <div className={cx('HomePage__secondPart_line')}>
                    <CurveLogo />
                  </div>
                </div>
              </Heading>
            </Container>
            <MainSlider homePageInnerRef={homePageInnerRef} />
          </>
        )}
        <Container
          className={cx('HomePage__container', {
            HomePage__container_waitOpenings: isWaitingForOpenings,
          })}
        >
          {isPhone && <AddressButton />}
          {isMobile ? (
            <>
              {!stockRole && <CategoryNavMobile />}
              {!isWaitingForOpenings && <FloatingCart />}
              {!isWaitingForOpenings && <PromocodeWidget className={cx('HomePage__promocode')} />}
            </>
          ) : (
            <>
              {!stockRole && <CategoryNavDesktop className={cx('HomePage__menu')} />}
              {!isWaitingForOpenings && <MainSidebar />}
            </>
          )}
          <section
            className={cx('HomePage__content', {
              HomePage__content_waitOpenings: isWaitingForOpenings,
            })}
            ref={homePageInnerRef}
          >
            {stockRole ? (
              <StockGoods stockRole={stockRole} />
            ) : (
              <Catalog isHiddenActions={isWaitingForOpenings} />
            )}
          </section>
        </Container>
        <DeliveryAlert />
        <FreeSaucesPanel />
        <ThanksForOrderingLink />
        {isWaitingForOpenings && <NotAllowedCityModal />}
      </div>
    </>
  );
}

HomePage.defaultProps = {
  stockRole: '',
};

HomePage.propTypes = {
  stockRole: PropTypes.oneOf(['conditions', 'rewards', '']),
};
